/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, Table, TableBody,
  TableCell, TableContainer, TableRow, TextField, Switch, FormControlLabel,
  Divider, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';

const TEAL = '#00798E';
const TH = { py: 1.5, px: 2, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, color: '#999', borderBottom: '1.5px solid #f0f2f5', whiteSpace: 'nowrap', letterSpacing: '0.06em' };
const td = (last) => ({ py: 1.25, px: 2, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.05)', fontSize: '0.8rem' });

const emptyForm = () => ({
  name: '', sub_title: '', address: '', city: '', state: '', pincode: '',
  gstin: '', gstin_uin: '', pan_no: '', state_code: '', dl_no: '',
  phone: '', email: '',
  bank_name: '', account_no: '', ifsc_code: '', bank_branch: '',
  declaration: '', is_default: false, is_active: true,
});

function PharmacyFormDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '',
        sub_title: editing.sub_title || '',
        address: editing.address || '',
        city: editing.city || '',
        state: editing.state || '',
        pincode: editing.pincode || '',
        gstin: editing.gstin || '',
        gstin_uin: editing.gstin_uin || '',
        pan_no: editing.pan_no || '',
        state_code: editing.state_code || '',
        dl_no: editing.dl_no || '',
        phone: editing.phone || '',
        email: editing.email || '',
        bank_name: editing.bank_name || '',
        account_no: editing.account_no || '',
        ifsc_code: editing.ifsc_code || '',
        bank_branch: editing.bank_branch || '',
        declaration: editing.declaration || '',
        is_default: editing.is_default === 'Y',
        is_active: editing.is_active !== 'N',
      });
    } else {
      setForm(emptyForm());
    }
  }, [editing, open]);

  const up = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value.toUpperCase() }));
  const upRaw = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { Swal.fire({ icon: 'warning', title: 'Name is required' }); return; }
    const payload = {
      name: form.name,
      sub_title: form.sub_title || null,
      address: form.address || null,
      city: form.city || null,
      state: form.state || null,
      pincode: form.pincode || null,
      gstin: form.gstin || null,
      gstin_uin: form.gstin_uin || null,
      pan_no: form.pan_no || null,
      state_code: form.state_code || null,
      dl_no: form.dl_no || null,
      phone: form.phone || null,
      email: form.email || null,
      bank_name: form.bank_name || null,
      account_no: form.account_no || null,
      ifsc_code: form.ifsc_code || null,
      bank_branch: form.bank_branch || null,
      declaration: form.declaration || null,
      is_default: form.is_default ? 'Y' : 'N',
      is_active: form.is_active ? 'Y' : 'N',
    };
    try {
      setSaving(true);
      if (editing) {
        await api.pharmacy.updatePharmacy(editing.id, payload);
      } else {
        await api.pharmacy.createPharmacy(payload);
      }
      onSaved();
      onClose();
    } catch { /* ApiClient shows Swal */ } finally { setSaving(false); }
  };

  const field = (label, key, opts = {}) => (
    <TextField
      size="small" fullWidth label={label}
      value={form[key]}
      onChange={opts.raw ? upRaw(key) : up(key)}
      sx={{ '& .MuiOutlinedInput-root': { height: 36, fontSize: '0.82rem' } }}
    />
  );

  const SectionLabel = ({ children }) => (
    <Grid item xs={12}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: TEAL, textTransform: 'uppercase', letterSpacing: 0.8, mt: 0.5, pb: 0.5, borderBottom: `1.5px solid ${TEAL}22` }}>
        {children}
      </Typography>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
        {editing ? 'Edit Pharmacy' : 'New Pharmacy'}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Basic info */}
          <SectionLabel>Basic Information</SectionLabel>
          <Grid item xs={12} sm={8}>{field('Pharmacy / Shop Name *', 'name')}</Grid>
          <Grid item xs={12} sm={4}>{field('Sub Title / Shop Type', 'sub_title')}</Grid>
          <Grid item xs={12} sm={4}>{field('City', 'city')}</Grid>
          <Grid item xs={12} sm={5}>{field('State', 'state')}</Grid>
          <Grid item xs={12} sm={3}>{field('Pincode', 'pincode')}</Grid>
          <Grid item xs={12}>{field('Address', 'address')}</Grid>
          <Grid item xs={12} sm={6}>{field('Phone', 'phone', { raw: true })}</Grid>
          <Grid item xs={12} sm={6}>{field('Email', 'email', { raw: true })}</Grid>

          {/* GST / Invoice details */}
          <SectionLabel>GST &amp; Invoice Details</SectionLabel>
          <Grid item xs={12} sm={6}>{field('GSTIN', 'gstin')}</Grid>
          <Grid item xs={12} sm={6}>{field('GSTIN / UIN', 'gstin_uin')}</Grid>
          <Grid item xs={12} sm={4}>{field('PAN No', 'pan_no')}</Grid>
          <Grid item xs={12} sm={4}>{field('GST State Code (e.g. 19)', 'state_code')}</Grid>
          <Grid item xs={12} sm={4}>{field('Drug License No (DL No)', 'dl_no', { raw: true })}</Grid>

          {/* Bank details */}
          <SectionLabel>Bank Details (for Invoice Footer)</SectionLabel>
          <Grid item xs={12} sm={6}>{field('Bank Name', 'bank_name', { raw: true })}</Grid>
          <Grid item xs={12} sm={6}>{field('Branch', 'bank_branch', { raw: true })}</Grid>
          <Grid item xs={12} sm={6}>{field('Account No', 'account_no', { raw: true })}</Grid>
          <Grid item xs={12} sm={6}>{field('IFSC Code', 'ifsc_code')}</Grid>

          {/* Declaration */}
          <SectionLabel>Declaration (Invoice Footer)</SectionLabel>
          <Grid item xs={12}>
            <TextField size="small" fullWidth multiline rows={3} label="Declaration Text"
              value={form.declaration} onChange={(e) => setForm(p => ({ ...p, declaration: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
            />
          </Grid>

          {/* Flags */}
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch checked={form.is_default} onChange={(e) => setForm((p) => ({ ...p, is_default: e.target.checked }))} size="small" sx={{ '& .MuiSwitch-thumb': { bgcolor: form.is_default ? TEAL : undefined } }} />}
              label={<MDTypography variant="caption">Set as Default</MDTypography>}
            />
          </Grid>
          {editing && (
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} size="small" />}
                label={<MDTypography variant="caption">Active</MDTypography>}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#005e6e' }, textTransform: 'none', fontWeight: 600, boxShadow: 'none', color: '#fff !important' }}
        >
          {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PharmaciesScreen() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { selectPharmacy } = usePharmacy();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const r = await api.pharmacy.listPharmacies(); setRows(r.data || []); }
    catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (row) => {
    const { isConfirmed } = await Swal.fire({
      title: `Delete "${row.name}"?`, icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete',
    });
    if (!isConfirmed) return;
    try { await api.pharmacy.deletePharmacy(row.id); fetchData(); } catch { }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Banner */}
        <Box sx={{ mb: 2.5, p: 2.5, borderRadius: 2, background: `linear-gradient(135deg, ${TEAL} 0%, #004f5e 100%)`, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalPharmacyIcon sx={{ fontSize: 40, opacity: 0.85 }} />
          <Box>
            <MDTypography variant="h5" sx={{ color: 'white !important', fontWeight: 800, lineHeight: 1.2 }}>Pharmacies</MDTypography>
            <MDTypography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
              Manage pharmacy branches · Each pharmacy has its own inventory, inward and reports
            </MDTypography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 700, color: '#fff !important', backdropFilter: 'blur(4px)' }}
          >
            New Pharmacy
          </Button>
        </Box>

        <Card>
          <TableContainer>
            <Table size="small">
              <TableRow>
                {['Code', 'Name', 'City / State', 'GSTIN', 'Phone', 'Status', ''].map((h, i) => (
                  <TableCell key={i} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress size={24} sx={{ color: TEAL }} /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: '#bbb', fontSize: '0.82rem' }}>No pharmacies yet</TableCell></TableRow>
                ) : rows.map((row, i) => {
                  const last = i === rows.length - 1;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontFamily: 'monospace', fontWeight: 700, color: TEAL, fontSize: '0.8rem' }}>{row.code}</Box>
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontWeight: 600 }}>{row.name}</Box>
                        {row.is_default === 'Y' && <Chip label="Default" size="small" color="info" sx={{ fontSize: '0.58rem', height: 16, mt: 0.3 }} />}
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box>{row.city || '—'}</Box>
                        <Box sx={{ fontSize: '0.7rem', color: '#aaa' }}>{row.state}</Box>
                      </TableCell>
                      <TableCell sx={{ ...td(last), fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.gstin || '—'}</TableCell>
                      <TableCell sx={td(last)}>{row.phone || '—'}</TableCell>
                      <TableCell sx={td(last)}>
                        <Chip
                          label={row.is_active === 'Y' ? 'Active' : 'Inactive'} size="small"
                          color={row.is_active === 'Y' ? 'success' : 'default'}
                          sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Select this pharmacy">
                            <IconButton size="small" onClick={() => selectPharmacy(row)} sx={{ color: TEAL }}>
                              <LocalPharmacyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditing(row); setDialogOpen(true); }}>
                              <EditIcon sx={{ fontSize: 15, color: '#888' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(row)}>
                              <DeleteIcon sx={{ fontSize: 15, color: '#d33' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </MDBox>

      <PharmacyFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchData}
        editing={editing}
      />
      <Footer />
    </DashboardLayout>
  );
}
